import type { PetCard } from "@highmarch/app";
import { checkHtml } from "../components";
import { petPortraitHtml } from "./petArt";
import { levelWords, pickWords } from "./petWords";

/**
 * The battle card's pet picker (Pets v3, docs/PETS.md "On screen": "the chip picks which pet comes along, or none"):
 * a tap on the chip opens it over the chip — every pet had, its portrait in its coat and its level, a check on the one
 * coming along, and "None" to go without. A tap picks and closes. Strings and one popover, like the treat picker
 * (treatWell.ts); features/pets.ts equips through @highmarch/app Pets and redraws the chip.
 */

/** The picker's markup: `cards` the pets had (roster order), `on` the one coming along (null: none). */
export function petPickHtml(cards: readonly PetCard[], on: string | null): string {
  const w = pickWords();
  const one = (c: PetCard): string => {
    const sel = c.pet.id === on;
    return `<button type="button" class="pp-one${sel ? " on" : ""}" data-pet="${c.pet.id}" aria-pressed="${sel}" aria-label="${c.pet.name}, ${levelWords(c)}">`
      + `${petPortraitHtml(c.pet.id, 40)}${sel ? checkHtml("supchk") : ""}<small>${c.pet.name}</small><b>${levelWords(c)}</b></button>`;
  };
  const none = `<button type="button" class="pp-one none${on === null ? " on" : ""}" data-pet="" aria-pressed="${on === null}"><span class="pp-x" aria-hidden="true">✕</span>${on === null ? checkHtml("supchk") : ""}<small>${w.none}</small><b>${w.noneSub}</b></button>`;
  return `<div class="petpick" role="dialog" aria-label="${w.title}"><p class="tp-h"><b class="disp">${w.title}</b><small>${w.sub}</small></p><div class="pp-row">${cards.map(one).join("")}${none}</div></div>`;
}

/**
 * Opens the picker over `anchor` (the chip), inside `root` (the hub or the map, so it goes with them); `pick(id)` on a
 * choice (a pet id, or null for none). A tap outside closes it. Returns its close.
 */
export function openPetPick(root: HTMLElement, anchor: HTMLElement, cards: readonly PetCard[], on: string | null, pick: (id: string | null) => void): () => void {
  document.querySelector(".petpick")?.remove();
  const host = document.createElement("div");
  host.innerHTML = petPickHtml(cards, on);
  const el = host.firstElementChild as HTMLElement;
  root.append(el);
  const a = anchor.getBoundingClientRect(), r = root.getBoundingClientRect(), w = el.offsetWidth || 300;
  el.style.left = `${Math.max(8, Math.min(r.width - w - 8, a.left - r.left))}px`;
  el.style.top = `${Math.max(8, a.top - r.top - (el.offsetHeight || 140) - 8)}px`;
  const close = (): void => { el.remove(); document.removeEventListener("pointerdown", outside, true); };
  const outside = (e: Event): void => { if (!el.contains(e.target as Node) && !anchor.contains(e.target as Node)) close(); };
  document.addEventListener("pointerdown", outside, true);
  for (const b of el.querySelectorAll<HTMLButtonElement>(".pp-one")) b.onclick = () => { close(); pick(b.dataset.pet ? b.dataset.pet : null); };
  return close;
}
